import React, { useEffect, useState } from "react";
import { FilePlus2, FileText, RefreshCw, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import { useAuth } from "../Auth";

const REPORT_TYPES = [
  { value: "rapport technique", label: "Rapport technique" },
  { value: "rapport d'etude theorique", label: "Rapport d'étude théorique" },
  { value: "autre", label: "Autre" },
];

const GuttenbergHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUseGuttenberg = Boolean(user?.can_use_guttenberg);
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState("");
  const [draft, setDraft] = useState({ title: "", client: "", report_type: "rapport technique" });
  const [deletingProjectId, setDeletingProjectId] = useState("");

  const load = async () => {
    setStatus("Chargement...");
    try {
      setProjects(await api.listProjects());
      setStatus("");
    } catch (error) {
      setStatus(error.message || "Erreur de chargement");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (event) => {
    event.preventDefault();
    if (!draft.title.trim()) return;
    setStatus("Création...");
    try {
      const project = await api.createProject({ ...draft, author: "Innowide" });
      navigate(`/tools/guttenberg/reports/${project.id}`);
    } catch (error) {
      setStatus(error.message || "Erreur de création");
    }
  };

  const deleteProject = async (project) => {
    if (!project?.id || deletingProjectId) return;
    const confirmed = window.confirm(`Supprimer définitivement le rapport "${project.title}" ?`);
    if (!confirmed) return;
    setDeletingProjectId(project.id);
    setStatus("Suppression du rapport...");
    try {
      await api.deleteProject(project.id);
      setProjects((current) => current.filter((item) => item.id !== project.id));
      setStatus("Rapport supprimé.");
    } catch (error) {
      setStatus(error.message || "Suppression impossible");
    } finally {
      setDeletingProjectId("");
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 py-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Guttenberg</p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Rapports techniques</h1>
          <p className="mt-1 text-sm text-slate-500">Sources, analyse, plan, rédaction et exports.</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:border-slate-300"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {!canUseGuttenberg && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Guttenberg est disponible en lecture seule. Un administrateur peut vous accorder le droit d'utilisation.
        </div>
      )}

      {canUseGuttenberg && <form onSubmit={create} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:grid-cols-[1fr_220px_220px_auto]">
        <input
          value={draft.title}
          onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
          placeholder="Titre du rapport"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          required
        />
        <input
          value={draft.client}
          onChange={(event) => setDraft((current) => ({ ...current, client: event.target.value }))}
          placeholder="Client"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <select
          value={draft.report_type}
          onChange={(event) => setDraft((current) => ({ ...current, report_type: event.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          {REPORT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800">
          <FilePlus2 className="h-4 w-4" />
          Créer
        </button>
      </form>}

      {status && <div className="text-sm text-slate-500">{status}</div>}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {projects.map((project) => (
          <article
            key={project.id}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-200 hover:shadow"
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate(`/tools/guttenberg/reports/${project.id}`)}
                className="min-w-0 flex-1 text-left"
                disabled={deletingProjectId === project.id}
              >
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-950">{project.title}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {project.client || "Client non renseigné"} · {project.source_files?.length || 0} source(s)
                    </div>
                    {project.deepflow_project_id && (
                      <div className="mt-3 inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        Projet Deepflow #{project.deepflow_project_id}
                      </div>
                    )}
                  </div>
                </div>
              </button>
              {canUseGuttenberg && <button
                type="button"
                onClick={() => deleteProject(project)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={Boolean(deletingProjectId)}
                title="Supprimer le rapport"
                aria-label={`Supprimer ${project.title}`}
              >
                <Trash2 className={`h-4 w-4 ${deletingProjectId === project.id ? "animate-pulse" : ""}`} />
              </button>}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default GuttenbergHome;
